// Lightweight Web Audio synth — no asset downloads needed.
// Falls back silently if AudioContext is unavailable.

let ctx = null;
let enabled = true;

const getCtx = () => {
  if (typeof window === "undefined") return null;
  if (!ctx) {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return null;
      ctx = new AC();
    } catch {
      return null;
    }
  }
  // Some browsers suspend the context until user gesture
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  return ctx;
};

const STORAGE_KEY = "pbo-sound-enabled";

export const initSound = () => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    enabled = v === null ? true : v === "1";
  } catch {
    enabled = true;
  }
};

export const isSoundEnabled = () => enabled;

export const setSoundEnabled = (v) => {
  enabled = !!v;
  try {
    localStorage.setItem(STORAGE_KEY, enabled ? "1" : "0");
  } catch {
    // ignore storage errors
  }
};

const tone = (freq, duration, type = "sine", gain = 0.05, attack = 0.005) => {
  if (!enabled) return;
  const c = getCtx();
  if (!c) return;
  const now = c.currentTime;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, now);
  g.gain.setValueAtTime(0.0001, now);
  g.gain.exponentialRampToValueAtTime(gain, now + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, now + duration);
  osc.connect(g).connect(c.destination);
  osc.start(now);
  osc.stop(now + duration + 0.02);
};

// Crisp "blip" when someone places a bid
export const playBid = () => {
  tone(880, 0.08, "triangle", 0.06);
  setTimeout(() => tone(1320, 0.08, "triangle", 0.05), 30);
};

// "Hammer drop" – two-stage low thunk + soft tail
export const playHammer = () => {
  if (!enabled) return;
  const c = getCtx();
  if (!c) return;
  const now = c.currentTime;
  // Knock
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = "square";
  osc.frequency.setValueAtTime(120, now);
  osc.frequency.exponentialRampToValueAtTime(40, now + 0.18);
  g.gain.setValueAtTime(0.0001, now);
  g.gain.exponentialRampToValueAtTime(0.16, now + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, now + 0.25);
  osc.connect(g).connect(c.destination);
  osc.start(now);
  osc.stop(now + 0.3);
  // Tail chime
  setTimeout(() => {
    tone(660, 0.25, "triangle", 0.05, 0.01);
    setTimeout(() => tone(880, 0.35, "triangle", 0.04), 60);
  }, 180);
};

// Soft chime when someone joins / nominate starts
export const playChime = () => {
  tone(523, 0.12, "sine", 0.04);
  setTimeout(() => tone(784, 0.18, "sine", 0.04), 80);
};

// Tick on the last few seconds of the timer
export const playTick = () => {
  tone(2200, 0.04, "square", 0.03);
};
